import React, { useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import DatePicker from "@services/ui-components/src/DatePicker/DatePicker";
import { useNavigate, useParams } from "react-router-dom";
import { Col, OverlayTrigger, Tooltip, Button, Row, Popover } from "react-bootstrap";
import i18next from "i18next";
import { ErrorModalComponent } from "@me-pos/error-modal";
import { EmployeeLimitModal, SpinnerLoader, TableHeader, TextareaInputWithCounter } from "@services/ui-components";
import CreatePositionModal from "../ui-components/CreatePositionModal/CreatePositionModal";
import VioletBadge from "@services/ui-components/src/VioletBadge/VioletBadge";
import Spinner from "@services/ui-components/src/Spinner/Spinner";
import { addEmployeeFormValidationSchema } from "./constants/constants";
import CustomCreatableReactSelect from "@services/ui-components/src/select/CustomCreatableReactSelect";
import PhonePicker from "@services/ui-components/src/PhonePicker/PhonePicker";
import ImageCrop from "@services/ui-components/src/ImageCrop/ImageCrop";
import DropItem from "@services/ui-components/src/DropItem/DropItem";
import AccessService from "@me-team/host/services/AccessService";
import DropPhotosMultiply from "../DropPhotosMultiply/DropPhotosMultiply";
import ErrorService from "../../services/ErrorService";
import { cloneNameToDisplay } from "../Me-menu/utils/cloneNameUtils";
import CustomReactSelect from "@services/ui-components/src/select/CustomReactSelect";
import { ApolloError, useReactiveVar } from "@apollo/client";
import { useGetCompanyIdQuery, useGetEmployeesQuery } from "@me-team/host/main/employeeList/graphql/employeeList.hooks";
import {
    useCreateEmployeeMutation,
    useGetEmployeesPositionsQuery,
    useGetRolesQuery,
    useGetSpecSpecializationQuery,
} from "@me-team/host/main/addEmployee/graphql/addEmployee.hooks";
import JoyrideWrapper from "../Joyride/JoyrideWrapper";
import { joyrideService } from "../Joyride/joyrideService";
import { useGetCountryGeoQuery } from "@me-team/host/main/branches/graphql/branches.hooks";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import ScrollToErrorHelper from "../../services/ScrollToErrorHelper/ScrollToErrorHelper";

const AddEmployee: React.FC = () => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const navigate = useNavigate();
    const runJoy = localStorage.getItem("run") === "true";
    const [run] = useState(runJoy);
    const step = localStorage.getItem("step") || "0";
    const [stepIndex, setStepIndex] = useState(+step);

    const [startDate, setStartDate] = useState<Date>(null);
    const [date] = useState("");
    const [showPositionModal, setShowPositionModal] = useState(false);
    const [photosQualification, setPhotosQualification] = useState([null]);
    const [show, setShow] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState(null);
    const [newPossition, setNewPossition] = useState(null);
    const [key, setKey] = useState(0);
    const [, setCountryId] = useState<number | null>(null);
    const canCreatePosition = useRoleAccess(CompanyAccess.PositionCreate);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    const accessService = AccessService.getInstance().isPremium;

    const { data: countryGeoData, loading: loadingCountryGeo } = useGetCountryGeoQuery({
        onCompleted(data) {
            if (data?.countryGeo?.country?.id) {
                setCountryId(data.countryGeo.country.id);
            }
        },
        skip: !currentCompanyId,
        context: {
            errorType: "local",
        },
        onError: (error) => {
            console.error(error);
        },
    });

    const { data, loading, refetch } = useGetEmployeesQuery({
        variables: { page: 1, itemsPerPage: 10000, companyId: +currentCompanyId, branchId: Number(branchId) },
        skip: !currentCompanyId,
    });
    const { data: dataSpecialization, loading: loadingSpecialization } = useGetSpecSpecializationQuery({
        fetchPolicy: "network-only",
        variables: { branchId: Number(branchId) },
        skip: !currentCompanyId,
        context: {
            errorType: "local",
        },
        onError: (error) => {
            console.error(error);
        },
    });
    const { data: roles, loading: loadingRoles } = useGetRolesQuery({
        variables: {
            branchId: Number(branchId),
            page: 1,
            itemsPerPage: 10000,
            excludeArchived: true,
        },
        context: {
            errorType: "local",
        },
        skip: !currentCompanyId,
        onError: (error) => {
            console.error(error);
        },
    });

    const countryGeo = countryGeoData?.countryGeo?.country;
    const specialization = dataSpecialization?.employeeSpecializations;
    const handleChange = (date: string | null | Date, setFieldValue: (field: string, value: string) => void) => {
        if (date) {
            const formattedDate = new Date(date);
            const year = formattedDate.getFullYear();
            const month = String(formattedDate.getMonth() + 1).padStart(2, "0");
            const day = String(formattedDate.getDate()).padStart(2, "0");
            const formattedDateString = `${year}-${month}-${day}`;
            setStartDate(formattedDate);
            setFieldValue("dateOfBirth", formattedDateString);
        } else {
            setStartDate(null);
            setFieldValue("dateOfBirth", null);
        }
    };

    const {
        data: possitions,
        loading: loadingPossitions,
        refetch: refetchPositions,
    } = useGetEmployeesPositionsQuery({
        variables: { companyId: +currentCompanyId },
        skip: !currentCompanyId,
        context: {
            errorType: "local",
        },
        onError: (error) => {
            console.error(error);
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const { data: companyId, loading: loadingCompanyId } = useGetCompanyIdQuery({
        variables: { companyId: +currentCompanyId },
        skip: !currentCompanyId,
        onError: (error) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
        context: {
            errorType: "global",
        },
    });

    const [createEmployee] = useCreateEmployeeMutation({
        context: {
            errorType: "local",
        },
    });

    const handlePositionModal = () => {
        setShowPositionModal((prevState) => !prevState);
        setKey((prevKey) => prevKey + 1);
    };
    const employees = data?.user?.company?.employees?.employees;

    const handleConfirm = (handleSubmit: (value: any) => void, setConfirming: (value: boolean) => void): void => {
        handleSubmit(true);
        setConfirming(true);
    };
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() - 14);
    const goToNextStep = (id: number) => {
        joyrideService.setUser({ id: id });
        const nextStepIndex = stepIndex + (accessService ? 2 : 1);
        localStorage.setItem("step", nextStepIndex.toString());
        setStepIndex(nextStepIndex);
        navigate(`/employees-list/employee/${id}/${branchId}`, { state: { text: t("Created"), toast: true } });
    };
    const handleCancel = () => {
        if (run && stepIndex === 2) {
            localStorage.setItem("step", "1");
            navigate(`/employees-list/${branchId}`);
        } else {
            navigate(`/employees-list/${branchId}`);
        }
    };

    if (
        loadingCountryGeo ||
        loadingPossitions ||
        loadingCompanyId ||
        loading ||
        loadingSpecialization ||
        loadingRoles ||
        !currentCompanyId
    )
        return <Spinner />;

    const permissionsRoleOptions = roles?.role?.roles
        ?.filter((role) => !role.isOwner)
        .map((role) => ({ ...role, name: cloneNameToDisplay(role.name, t) }));

    return (
        <>
            <VioletBadge>
                {t(
                    "Required fields are marked with an asterisk *. The data that customers see in the online record form is marked "
                )}{" "}
                <i className="bi bi-info-circle"></i>
            </VioletBadge>
            <TableHeader
                title={t("Add an employee")}
                paths={[
                    { path: `/employees-list/add-employee/${branchId}`, label: "Personnel management" },
                    {
                        path: `/employees-list/${branchId}`,
                        label: "List of employees",
                    },
                    { path: `/employees-list/add-employee/${branchId}`, label: "Add an employee" },
                ]}
            />
            <div className="add-employee-step">
                <Formik
                    initialValues={{
                        company: companyId?.user.company.id,
                        name: "",
                        surname: "",
                        patronymic: "",
                        dateOfBirth: date,
                        email: "",
                        phone: countryGeo?.phoneCode?.code ? `+${countryGeo?.phoneCode?.code}` : "",
                        passportData: "",
                        itn: "",
                        aboutMe: "",
                        employeePosition: null,
                        specialization: "",
                        role: null,
                        status: 3,
                        manualBlocking: true,
                        tariffBlocking: true,
                        showSurnameInBooking: true,
                    }}
                    validationSchema={addEmployeeFormValidationSchema(t)}
                    onSubmit={async (values, { setSubmitting, setErrors }) => {
                        try {
                            const cleanedPhotosQualification = photosQualification?.filter((photo) => photo != null);

                            const updatedValues = {
                                ...values,
                                email: values.email?.trim() || "",
                                qualificationConfirmations: [...cleanedPhotosQualification],
                            };

                            const newRoleIsOwner =
                                roles?.role?.roles.find((r) => r.id === values.role)?.isOwner ?? false;
                            if (!AccessService.getInstance().canAddEmployee(employees, newRoleIsOwner) && !confirming) {
                                handleShow();
                            } else {
                                const response = await createEmployee({
                                    variables: {
                                        companyId: companyId?.user.company.id,
                                        input: updatedValues,
                                        branchId: +branchId,
                                    },
                                    context: {
                                        errorType: "local",
                                    },
                                    onCompleted: () => {
                                        refetch();
                                    },
                                    onError: (error: ApolloError) => {
                                        setIsErrorModalOpen(ErrorService.errorHandling(error));
                                        throw error;
                                    },
                                });
                                run
                                    ? goToNextStep(+response.data.createEmployee.id)
                                    : navigate(`/employees-list/${branchId}`, {
                                          state: {
                                              employeeId: +response.data.createEmployee.id,
                                              text: t("Created"),
                                              toast: true,
                                          },
                                      });
                            }
                        } catch (error) {
                            console.error("ERROR", error);

                            const graphQLError = error.graphQLErrors?.[0];

                            if (
                                graphQLError?.extensions?.validation?.phone?.[0]?.message ===
                                "This value is not a valid phone number."
                            ) {
                                setErrors({ phone: t("Invalid phone number") });
                            } else if (graphQLError?.message === "Employee already exists") {
                                console.error("Error!", graphQLError.message);
                                setErrors({ email: `Email  ${values.email} ${t("already exists in system.")}` });
                            }
                        } finally {
                            setSubmitting(false);
                        }
                    }}
                >
                    {({
                        errors,
                        touched,
                        setFieldValue,
                        values,
                        handleBlur,
                        handleSubmit,
                        isSubmitting,
                        submitCount,
                    }) => {
                        return (
                            <>
                                <ScrollToErrorHelper errors={errors} submitClickedCount={submitCount} />
                                <CreatePositionModal
                                    show={showPositionModal}
                                    handleModal={handlePositionModal}
                                    companyId={companyId?.user.company.id}
                                    refetch={refetchPositions}
                                    setNewPossition={setNewPossition}
                                    setFieldValue={setFieldValue}
                                />
                                <Form onSubmit={handleSubmit}>
                                    <div className="row">
                                        <div className="d-flex flex-column flex-lg-row">
                                            <Col xs={12} lg={9} className="me-0">
                                                <div className="row">
                                                    <h4 className="fw-normal">{t("Personal data")}</h4>
                                                </div>
                                                <div className="row mt-sm-4">
                                                    <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                        <span className="text-dark fw-bold">
                                                            <small>
                                                                <label
                                                                    htmlFor="addEmployee-name-field"
                                                                    className="mb-0 text-dark form-label required-label"
                                                                >
                                                                    {t("Name")}
                                                                </label>
                                                            </small>
                                                        </span>
                                                    </div>
                                                    <div className="col-sm-9 col-md-10 pe-lg-5">
                                                        <div className="position-relative">
                                                            <Field
                                                                type="text"
                                                                name="name"
                                                                placeholder={t("Add Name")}
                                                                id="addEmployee-name-field"
                                                                maxLength="50"
                                                                className={`fs-7 form-control ${errors.name && touched.name ? "border-danger" : ""}`}
                                                            />

                                                            <div
                                                                className="position-absolute top-50 end-0 translate-middle-y pe-6"
                                                                style={{ height: "16px" }}
                                                            >
                                                                <OverlayTrigger
                                                                    overlay={
                                                                        <Tooltip>
                                                                            {t(
                                                                                "This data is displayed in the Online Booking form"
                                                                            )}
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <div className=" position-absolute top-50 translate-middle-y end-0 me-3">
                                                                        <i className="bi bi-info-circle"></i>
                                                                    </div>
                                                                </OverlayTrigger>
                                                            </div>
                                                        </div>
                                                        <ErrorMessage name="name">
                                                            {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                        </ErrorMessage>
                                                    </div>
                                                </div>

                                                <div className="row mt-sm-4">
                                                    <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                        <span className="text-dark fw-bold">
                                                            <small>
                                                                <label
                                                                    htmlFor="addEmployee-surname-field"
                                                                    className="mb-0 text-dark form-label required-label"
                                                                >
                                                                    {t("Last name")}
                                                                </label>
                                                            </small>
                                                        </span>
                                                    </div>
                                                    <div className="col-sm-9 col-md-10 pe-lg-5">
                                                        <div className="position-relative">
                                                            <Field
                                                                type="text"
                                                                name="surname"
                                                                id="addEmployee-surname-field"
                                                                placeholder={t("Add Last name")}
                                                                className={`fs-7 form-control ${errors.surname && touched.surname ? "border-danger" : ""}`}
                                                                maxLength="50"
                                                            />
                                                            <div className="position-absolute top-50 end-0 translate-middle-y ">
                                                                <OverlayTrigger
                                                                    overlay={
                                                                        <Tooltip>
                                                                            {t(
                                                                                "This data is displayed in the Online Booking form"
                                                                            )}
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <div className=" position-absolute top-50 translate-middle-y end-0 me-3">
                                                                        <i className="bi bi-info-circle"></i>
                                                                    </div>
                                                                </OverlayTrigger>
                                                            </div>
                                                        </div>
                                                        <ErrorMessage name="surname">
                                                            {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                        </ErrorMessage>
                                                    </div>
                                                </div>
                                                <div className="row mt-2">
                                                    <div className="offset-sm-3 offset-md-2 col-sm-9 col-md-10 pe-lg-5">
                                                        <div className="position-relative d-flex">
                                                            <Field
                                                                type="checkbox"
                                                                name="showSurnameInBooking"
                                                                id="addEmployee-showSurnameInBooking-checkbox"
                                                                className="form-check-input"
                                                                checked={!values.showSurnameInBooking}
                                                                onChange={() => {
                                                                    setFieldValue(
                                                                        "showSurnameInBooking",
                                                                        !values.showSurnameInBooking
                                                                    );
                                                                }}
                                                            />
                                                            <label
                                                                className="form-check-label ms-2 fs-7"
                                                                htmlFor="addEmployee-showSurnameInBooking-checkbox"
                                                            >
                                                                {t("Do not display Surname in Online-booking form")}
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="row mt-sm-4">
                                                    <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                        <span className="text-dark fw-bold">
                                                            <small>
                                                                <label
                                                                    htmlFor="addEmployee-middleName-field"
                                                                    className="mb-0 text-dark form-label required"
                                                                >
                                                                    {t("Middle name")}
                                                                </label>
                                                            </small>
                                                        </span>
                                                    </div>
                                                    <div className="col-sm-9 col-md-10 pe-lg-5">
                                                        <div className="position-relative">
                                                            <Field
                                                                type="text"
                                                                name="patronymic"
                                                                id="addEmployee-middleName-field"
                                                                placeholder={t("Add Middle name")}
                                                                className="fs-7 form-control"
                                                                maxLength="50"
                                                            />
                                                        </div>
                                                        <ErrorMessage name="patronymic">
                                                            {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                        </ErrorMessage>
                                                    </div>
                                                </div>

                                                <div className="row mt-sm-4">
                                                    <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                        <span className="text-dark fw-bold">
                                                            <small>
                                                                <label
                                                                    htmlFor="addEmployee-date-field"
                                                                    className="mb-0 text-dark form-label required"
                                                                >
                                                                    {t("Date of birth")}{" "}
                                                                </label>
                                                            </small>
                                                        </span>
                                                    </div>
                                                    <div className="col-sm-9 col-md-10 pe-lg-5">
                                                        <div className="position-relative">
                                                            <DatePicker
                                                                id="addEmployee-date-field"
                                                                name="dateOfBirth"
                                                                className={`form-control ${errors.dateOfBirth && touched.dateOfBirth ? "border-danger" : ""} w-100`}
                                                                wrapperClassName="w-100"
                                                                placeholderText={t("dd.mm.yyyy")}
                                                                selected={startDate}
                                                                onChange={(date) => handleChange(date, setFieldValue)}
                                                                dateFormat="dd.MM.yyyy"
                                                                maxDate={new Date()}
                                                                yearDropdownItemNumber={124}
                                                                isClearable
                                                            />
                                                            <ErrorMessage name="dateOfBirth">
                                                                {(msg) => (
                                                                    <p className="fs-7 m-0 p-1 text-danger">{msg}</p>
                                                                )}
                                                            </ErrorMessage>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="row mt-sm-4">
                                                    <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                        <span className="text-dark fw-bold">
                                                            <small>
                                                                <label
                                                                    htmlFor="addEmployee-E-mail-field"
                                                                    className="mb-0 text-dark form-label required-label"
                                                                >
                                                                    {t("E-mail")}
                                                                </label>
                                                            </small>
                                                        </span>
                                                    </div>
                                                    <div className="col-sm-9 col-md-10 pe-lg-5">
                                                        <div className="position-relative">
                                                            <Field
                                                                type="email"
                                                                name="email"
                                                                id="addEmployee-E-mail-field"
                                                                placeholder={t("Add e-mail")}
                                                                className={`fs-7 form-control ${errors.email && touched.email ? "border-danger" : ""}`}
                                                                maxLength="50"
                                                                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                                                                    const trimmedValue = e.target.value.trim();
                                                                    e.target.value = trimmedValue;
                                                                    setFieldValue("email", trimmedValue, false);
                                                                    handleBlur(e);
                                                                }}
                                                            />
                                                            <div className="position-absolute top-50 end-0 translate-middle-y ">
                                                                <i
                                                                    className="bi bi-shield-check"
                                                                    style={{ paddingRight: "12px" }}
                                                                >
                                                                    {" "}
                                                                </i>
                                                            </div>
                                                        </div>
                                                        <ErrorMessage name="email">
                                                            {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                        </ErrorMessage>
                                                    </div>
                                                </div>

                                                <div className="row mt-sm-4">
                                                    <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                        <span className="text-dark fw-bold">
                                                            <small>
                                                                <label
                                                                    htmlFor="addEmployee-phone-field"
                                                                    className="mb-0 text-dark form-label required-label"
                                                                >
                                                                    {t("Phone")}
                                                                </label>
                                                            </small>
                                                        </span>
                                                    </div>
                                                    <div className="col-sm-9 col-md-10 pe-lg-5">
                                                        <PhonePicker
                                                            name="phone"
                                                            id="addEmployee-phone-field"
                                                            value={values.phone}
                                                        />
                                                        <ErrorMessage name="phone">
                                                            {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                        </ErrorMessage>
                                                    </div>
                                                </div>

                                                <div className="row mt-sm-4">
                                                    <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                        <span className="text-dark fw-bold">
                                                            <small>
                                                                <label
                                                                    htmlFor="addEmployee-passportData-field"
                                                                    className="mb-0 text-dark form-label required"
                                                                >
                                                                    {t("Passport Data")}
                                                                </label>
                                                            </small>
                                                        </span>
                                                    </div>
                                                    <div className="col-sm-9 col-md-10 pe-lg-5">
                                                        <div className="position-relative">
                                                            <Field
                                                                type="text"
                                                                name="passportData"
                                                                placeholder={t("Enter Passport Data")}
                                                                className="fs-7 form-control"
                                                                maxLength="10"
                                                                id="addEmployee-passportData-field"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="col-sm-9 col-md-10 ms-auto">
                                                        <ErrorMessage name="passportData">
                                                            {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                        </ErrorMessage>
                                                        <span className="text-grey fs-7">
                                                            {" "}
                                                            {values.passportData.length}/10
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="row mt-sm-4">
                                                    <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                        <span className="text-dark fw-bold">
                                                            <small>
                                                                <label
                                                                    htmlFor="addEmployee-SSN-field"
                                                                    className="mb-0 text-dark form-label"
                                                                >
                                                                    {t("SSN (Social Security Number)")}{" "}
                                                                </label>
                                                            </small>
                                                        </span>
                                                    </div>
                                                    <div className="col-sm-9 col-md-10 pe-lg-5">
                                                        <div className="position-relative">
                                                            <Field
                                                                type="text"
                                                                name="itn"
                                                                placeholder={t("Enter SSN")}
                                                                id="addEmployee-SSN-field"
                                                                className="fs-7 form-control"
                                                                maxLength="20"
                                                            />
                                                        </div>
                                                        <ErrorMessage name="itn">
                                                            {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                        </ErrorMessage>
                                                    </div>
                                                </div>

                                                <div className="row mt-sm-4">
                                                    <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                        <span className="text-dark fw-bold">
                                                            <small>
                                                                <label
                                                                    htmlFor="addEmployee-About-field"
                                                                    className="mb-0 text-dark form-label"
                                                                >
                                                                    {t("About")}
                                                                </label>
                                                            </small>
                                                        </span>
                                                    </div>
                                                    <div className="col-sm-9 col-md-10 pe-lg-5">
                                                        <TextareaInputWithCounter
                                                            id="addEmployee-About-field"
                                                            name="aboutMe"
                                                            maxLength={400}
                                                            placeholder={t(
                                                                "Enter a short description for the master presentation"
                                                            )}
                                                            disabledLabel
                                                            inputColSm={12}
                                                            infoText="This data is displayed in the Online Booking form"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="row mt-5">
                                                    <h4 className="text-dark fw-normal">
                                                        {t("Professional information")}
                                                    </h4>
                                                </div>
                                                <div className="row mt-sm-4">
                                                    <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                        <span className="text-dark fw-bold">
                                                            <small>
                                                                <label
                                                                    htmlFor="position-field"
                                                                    className="mb-0 text-dark form-label required-label"
                                                                >
                                                                    {t("Position")}
                                                                </label>
                                                            </small>
                                                        </span>
                                                    </div>
                                                    <div className="col-sm-9 col-md-10 pe-lg-5">
                                                        <div className="position-relative">
                                                            <div className="d-flex align-items-center w-100">
                                                                <div className="w-100">
                                                                    <div className="position-relative w-100">
                                                                        <CustomReactSelect
                                                                            key={key}
                                                                            optionsArray={
                                                                                possitions?.user.company
                                                                                    .employeePositions.employeePositions
                                                                            }
                                                                            placeholder={t("Select Position")}
                                                                            setField={setFieldValue}
                                                                            fieldName="employeePosition"
                                                                            value="id"
                                                                            isTooltip={true}
                                                                            initialValue={newPossition}
                                                                            name={"position-field"}
                                                                            id={"addEmployee-Position-field"}
                                                                        />

                                                                        <div
                                                                            className="position-absolute top-50 end-0 translate-middle-y "
                                                                            style={{
                                                                                height: "16px",
                                                                                paddingRight: "12px",
                                                                            }}
                                                                        >
                                                                            <OverlayTrigger
                                                                                overlay={
                                                                                    <Popover>
                                                                                        <Popover.Body>
                                                                                            <Row>
                                                                                                <p className="m-0 mb-1">
                                                                                                    {t(
                                                                                                        "The position is displayed inside the system, and the specialization is in the form of an online record."
                                                                                                    )}{" "}
                                                                                                </p>
                                                                                            </Row>
                                                                                        </Popover.Body>
                                                                                    </Popover>
                                                                                }
                                                                            >
                                                                                <div className=" position-absolute top-50 translate-middle-y end-0 me-3">
                                                                                    <i className="bi bi-info-circle"></i>
                                                                                </div>
                                                                            </OverlayTrigger>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <Button
                                                                        type="button"
                                                                        onClick={handlePositionModal}
                                                                        variant="outline-gray"
                                                                        className=" text-dark ms-2  px-3 border square-btn"
                                                                        style={{ minWidth: "50px", height: "50px" }}
                                                                        disabled={!canCreatePosition}
                                                                    >
                                                                        <i className="bi bi-plus-lg"></i>
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                            <ErrorMessage name="employeePosition">
                                                                {(msg) => (
                                                                    <p className="fs-7 m-0 p-1 text-danger">{msg}</p>
                                                                )}
                                                            </ErrorMessage>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="row mt-sm-4">
                                                    <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                        <span className="text-dark fw-bold">
                                                            <small>
                                                                <label
                                                                    htmlFor="specialization-field"
                                                                    className="mb-0 text-dark form-label required-label text-break"
                                                                >
                                                                    {t("Specialization")}
                                                                </label>
                                                            </small>
                                                        </span>
                                                    </div>
                                                    <div className="col-sm-9 col-md-10 pe-lg-5">
                                                        <div className="position-relative">
                                                            <CustomCreatableReactSelect
                                                                optionsArray={specialization}
                                                                placeholder={t("Enter specialization")}
                                                                setField={setFieldValue}
                                                                fieldName="specialization"
                                                                value="name"
                                                                isTooltip={true}
                                                                name={"specialization-field"}
                                                                id={"addEmployee-specialization-field"}
                                                                maxLength={50}
                                                                createOptionLabel={t("Create a specialization")}
                                                                noOptionsText={t(
                                                                    "Start typing the specialization name"
                                                                )}
                                                                createOnBlur={true}
                                                            />

                                                            <div
                                                                className="position-absolute top-50 end-0 translate-middle-y pe-6"
                                                                style={{ height: "16px" }}
                                                            >
                                                                <OverlayTrigger
                                                                    overlay={
                                                                        <Tooltip>
                                                                            {t(
                                                                                "This data is displayed in the Online Booking form"
                                                                            )}
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <div className=" position-absolute top-50 translate-middle-y end-0 me-3">
                                                                        <i className="bi bi-info-circle"></i>
                                                                    </div>
                                                                </OverlayTrigger>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-sm-9 col-md-10 ms-auto">
                                                        <span className="text-grey fs-7">
                                                            {" "}
                                                            {values.specialization.length}/50
                                                        </span>
                                                        <ErrorMessage name="specialization">
                                                            {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                        </ErrorMessage>
                                                    </div>
                                                </div>

                                                <div className="row mt-sm-3">
                                                    <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                        <label
                                                            htmlFor="permissions-field"
                                                            className="form-label mb-0 text-dark fw-bold required-label"
                                                        >
                                                            <small className="text-break">
                                                                {t("Editing Permissions")}
                                                            </small>
                                                        </label>
                                                    </div>
                                                    <div className="col-sm-9 col-md-10 pe-lg-5">
                                                        <div className=" w-100">
                                                            <div className="position-relative">
                                                                <CustomReactSelect
                                                                    optionsArray={permissionsRoleOptions}
                                                                    placeholder={t("Choose a role")}
                                                                    setField={setFieldValue}
                                                                    fieldName="role"
                                                                    value="id"
                                                                    isTooltip={true}
                                                                    name={"permissions-field"}
                                                                    id={"addEmployee-permissions-field"}
                                                                />

                                                                <div
                                                                    className="position-absolute top-50 end-0 translate-middle-y pe-6"
                                                                    style={{ height: "16px" }}
                                                                >
                                                                    <OverlayTrigger
                                                                        overlay={
                                                                            <Popover>
                                                                                <Popover.Body>
                                                                                    <Row>
                                                                                        <p className="m-0 mb-1">
                                                                                            {" "}
                                                                                            {t(
                                                                                                '\"Editing permissions\" - defines what access the employee has.'
                                                                                            )}{" "}
                                                                                        </p>
                                                                                    </Row>
                                                                                    <Row>
                                                                                        <p className="m-0 mb-1">
                                                                                            {" "}
                                                                                            {t(
                                                                                                "Owner - unlimited access to all sections."
                                                                                            )}
                                                                                        </p>
                                                                                    </Row>
                                                                                    <Row>
                                                                                        <p className="m-0 mb-1">
                                                                                            {t(
                                                                                                'Administrator - access to all sections except "Tariff Management."'
                                                                                            )}
                                                                                        </p>
                                                                                    </Row>
                                                                                    <Row>
                                                                                        <p className="m-0 mb-1">
                                                                                            {t(
                                                                                                "Employee - has limited rights, he can only view his schedule, his record log, information about himself and the services he performs."
                                                                                            )}{" "}
                                                                                        </p>
                                                                                    </Row>
                                                                                </Popover.Body>
                                                                            </Popover>
                                                                        }
                                                                    >
                                                                        <div className="position-absolute top-50 translate-middle-y end-0 me-3">
                                                                            <i className="bi bi-info-circle"></i>
                                                                        </div>
                                                                    </OverlayTrigger>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <ErrorMessage name="role">
                                                            {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                        </ErrorMessage>
                                                    </div>
                                                </div>
                                                <div className="row mt-sm-4">
                                                    <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                        <span className="text-dark fw-bold">
                                                            <small>
                                                                <label className="mb-0 text-dark text-break form-label">
                                                                    {t("Qualification Confirmation")}
                                                                </label>
                                                            </small>
                                                        </span>
                                                    </div>

                                                    <div className="col-sm-9 col-md-10 pe-lg-5">
                                                        <div className="mb-0 ps-sm-1 mb-2 text-dark">
                                                            <span className="fs-7">
                                                                {t(
                                                                    "Add PNG, JPG, JPEG, WEBP images of diplomas, certificates"
                                                                )}{" "}
                                                            </span>
                                                            <OverlayTrigger
                                                                overlay={
                                                                    <Tooltip>
                                                                        {t(
                                                                            "This data is displayed in the Online Booking form"
                                                                        )}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <i
                                                                    className="bi bi-info-circle"
                                                                    style={{ paddingRight: "12px" }}
                                                                ></i>
                                                            </OverlayTrigger>
                                                        </div>
                                                        <Row className="m-0">
                                                            <DropPhotosMultiply
                                                                photosArray={photosQualification}
                                                                setPhotos={setPhotosQualification}
                                                            />
                                                        </Row>
                                                    </div>
                                                    <div className="col-sm-9 col-md-10 ms-auto">
                                                        <span className="text-grey fs-7 ">
                                                            {" "}
                                                            {t("Maximum size: 3 MB")}
                                                        </span>
                                                        <ErrorMessage name="fileRejection">
                                                            {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                        </ErrorMessage>
                                                    </div>
                                                </div>

                                                <div className="row mt-sm-4">
                                                    <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                        <span className="text-dark fw-bold">
                                                            <small>
                                                                <label className="mb-0 text-dark form-label required">
                                                                    {" "}
                                                                    {t("Contract")}
                                                                </label>
                                                            </small>
                                                        </span>
                                                    </div>

                                                    <div className="col-sm-9 col-md-10 pe-lg-5">
                                                        <DropItem
                                                            index={0}
                                                            handleFile={setFieldValue}
                                                            fieldName={"contract"}
                                                            file={true}
                                                        />
                                                    </div>
                                                    <div className="col-sm-9 col-md-10 ms-auto mt-2">
                                                        <span className="text-grey fs-7">
                                                            {t("Document formats: PDF, DOC, DOCX")}
                                                        </span>
                                                    </div>
                                                </div>
                                            </Col>

                                            <Col xs={12} lg={3} className="ps-lg-3 ps-0 mt-4 mt-lg-0">
                                                <h4 className="text-dark pb-3 mt-1">{t("Employee's photo")}</h4>
                                                <ImageCrop
                                                    setFieldValue={setFieldValue}
                                                    modalTitle={t("Employee's photo")}
                                                    notBGinfo
                                                />
                                            </Col>
                                        </div>

                                        <div className="d-sm-flex mb-5 mt-3">
                                            <Button
                                                variant="outline-primary"
                                                className="fw-normal mb-3 mb-sm-0 me-3 custom-btn rounded-1"
                                                onClick={() => handleCancel()}
                                            >
                                                {t("Cancel")}
                                            </Button>
                                            <Button
                                                type="submit"
                                                variant="primary"
                                                className="fw-bold custom-btn rounded-1"
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? (
                                                    <SpinnerLoader variant="light" loadingText={t("Saving...")} />
                                                ) : (
                                                    t("save")
                                                )}
                                            </Button>
                                        </div>
                                    </div>

                                    <EmployeeLimitModal
                                        showEmployeeLimitModal={show}
                                        handleCloseEmployeeLimitModal={handleClose}
                                        isSubmitting={isSubmitting}
                                        confirming={confirming}
                                        handleConfirmClick={() => handleConfirm(handleSubmit, setConfirming)}
                                        isCreateEmployee={true}
                                    />
                                </Form>
                            </>
                        );
                    }}
                </Formik>
            </div>

            {isErrorModalOpen && (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            )}
            {run && stepIndex === 2 && <JoyrideWrapper run={run} />}
        </>
    );
};

export default AddEmployee;
